import { useState, useRef, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import ReturnButtons from '../../components/core/ReturnButtons';
import { useTranslation } from 'react-i18next';
import { useErrorNotice } from '../../../hooks/useErrorNotice';
import { Actions as Snack } from '../../../store/ducks/snacks';
import { Visibility, VisibilityOff, ShowChart as ShowChartIcon } from '@material-ui/icons';
import { Box, Card, Button, Typography, IconButton, makeStyles, Tooltip } from '@material-ui/core';
import { ThemeProvider, useTheme } from '@material-ui/core/styles';
import GoogleMap from '../../components/map/GoogleMap';
import TideChartModal from '../../components/bathymetry/TideChartModal.jsx';
import {
  useResult,
  useFile,
  usePreProcessing,
  useRegisterGeoJsonExport,
  useRegisterPdfExport,
  useRegisterSessionExport,
  useThemeMode,
} from '../../contexts/index.jsx';
import ee from '../../../services/earth-engine';
import { getSelectedSatellite, mapGeeError, eeEval, eeUrl } from '../../../algorithms/utils/index.js';
import { Alert } from '@material-ui/lab';
import { ActivityIndicator } from '../../components/index.js';
import { buildSessionExport } from '../../../algorithms/bathymetry/sessionExport.js';
import { fetchDepthPoints } from '../../../algorithms/bathymetry/depthPoints';
import { buildModelFile, memberKey, treesOf } from '../../../algorithms/bathymetry/modelStore';
import { exportGeoJson } from '../../../algorithms/bathymetry/geoJsonExport';
import { buildBathymetryReport, REPORT_MODULE } from '../../../algorithms/bathymetry/pdfReport';
import { reportText } from '../../../algorithms/bathymetry/reportText';
import { loadImageForReport, renderReport, reportFileName } from '../../../common/pdfReport';
import { aoiPaths, aoiPoints } from '../../../common/aoi';
import { exportJSON, exportSession, extentOf, useLocalStorage } from '../../../common/utils.js';
import {
  buildMapStyles,
  createShorelineTheme,
  OVERLAY_BOTTOM_SAFE_AREA,
  MAP_CONTROLS_SAFE_AREA,
  OVERLAY_PANEL_WIDTH,
  OVERLAY_COLUMN_LAYOUT,
} from '../Shoreline/theme.js';
import ResultPanel from '../../components/bathymetry/ResultPanel.jsx';
import BackgroundMapPanel from '../../components/map/BackgroundMapPanel';
import OverlayPanelsToggle from '../../components/map/OverlayPanelsToggle';
import { planRegionParts, SAFE_PIXEL_LIMIT } from '../../../algorithms/bathymetry/tiling';
import { presetContextOf, presetMismatch } from '../../../algorithms/bathymetry/preset';
import { addPredDepthStd, getTrainingDepthReducer } from '../../../algorithms/bathymetry/depthImage';
import { downloadCSV, downloadEEImage } from '../../../algorithms/bathymetry/download';
import { computeDepthMetrics, formatMetricValue, metricsCsv } from '../../../algorithms/bathymetry/metrics';
import { fitAnalyticModel } from '../../../algorithms/bathymetry/analyticModels';
import { runTreeModel } from '../../../algorithms/bathymetry/treeRun';
import { collectPixelRows } from '../../../algorithms/bathymetry/pixelExport';
import {
  applyTideToDepth,
  hasTideSeries,
  mergeTidePoints,
  normalisedTideSeries,
  probeTidePoints,
} from '../../../algorithms/bathymetry/tideSeries';
import { isTreeModel, kindsFor } from '../../../algorithms/bathymetry/trees';
import { DEPTH_PALETTE, LONG_FIELDS } from '../../../algorithms/bathymetry/constants.js';

/**
 * @constant MAX_PREVIEW_SIDE
 * @description How many pixels the depth preview may have on the longer side of the area.
 * Earth Engine refuses a thumbnail whose pixels pass 48 MB, and at 10 m the bounds of the whole
 * Babitonga bay - 35 by 51 km - came to 113 MB ("Total request size (113087688 bytes) must be less
 * than or equal to 50331648 bytes"), whatever the detail of the KML. 2048 pixels keep that bay near
 * 18 MB.
 * @memberof module:Bathymetry
 */
const MAX_PREVIEW_SIDE = 2048;

/**
 * @constant PREVIEW_CRS
 * @description The projection of the depth preview. The map lays the preview over the area's
 * bounds as a ground overlay, which Google Maps stretches in Web Mercator; drawn in the survey's
 * UTM zone instead, the depth landed off the channels it belongs to - through the mangrove beside
 * the Rio Cascalho, in the Babitonga bay.
 * @memberof module:Bathymetry
 */
const PREVIEW_CRS = 'EPSG:3857';

/**
 * @function previewScale
 * @description The scale of the depth preview: the sensor's own resolution, unless the area is too
 * wide to be drawn at it in one thumbnail. A wider area is previewed coarser; the GeoTIFF, the CSV
 * and the metrics keep the sensor's resolution.
 * @param {Array} coordinates - The area as the app keeps it.
 * @param {Number} nativeScale - The sensor's resolution, in metres.
 * @memberof module:Bathymetry
 * @returns {Number} Metres per pixel.
 */
const previewScale = (coordinates, nativeScale) => {
  const points = aoiPoints(coordinates);
  if (!points.length || !window.google?.maps?.geometry) return nativeScale;
  const lngs = points.map(([lng]) => lng);
  const lats = points.map(([, lat]) => lat);
  const [[west, east], [south, north]] = [extentOf(lngs), extentOf(lats)];
  const edge = Math.abs(south) < Math.abs(north) ? south : north;
  const distance = (a, b) =>
    window.google.maps.geometry.spherical.computeDistanceBetween(
      new window.google.maps.LatLng(a[1], a[0]),
      new window.google.maps.LatLng(b[1], b[0]),
    );
  const longerSide = Math.max(distance([west, edge], [east, edge]), distance([west, south], [west, north]));
  return Math.max(nativeScale, longerSide / MAX_PREVIEW_SIDE);
};

/**
 * @function BathymetryResultContent
 * @description The result screen: runs the chosen model over the imagery, draws the modelled
 * depth on the map, and offers the run as CSV, GeoTIFF, GeoJSON, PDF and a saved model.
 * Everything the run itself does lives in `algorithms/bathymetry`; what stays here is the wiring
 * - state, messages and the map.
 * Deliberately kept separate from `ProcessingPage`, which only builds the module theme and
 * provides it: `makeStyles` resolves `theme` from the context where the hook is *called*, not
 * where its JSX renders, so a component that both called `useStyles()` and rendered its own
 * `<ThemeProvider>` kept the light palette on its panels while the MUI components inside them
 * followed the dark one - white on white. Calling `useStyles()` one level below the provider is
 * what makes the dark palette reach the panels.
 * **Called by:
 * @see {ProcessingPage}
 * @memberof module:Bathymetry
 * @returns {JSX.Element}
 */
const BathymetryResultContent = () => {
  const classes = useStyles();
  const [t] = useTranslation();
  const notifyError = useErrorNotice();
  const dispatch = useDispatch();
  const acquisitionCoordinates = useSelector((state) => state.acquisition.coordinates);
  const { roiRef } = usePreProcessing();
  const { fileData } = useFile();
  const coordinates = fileData?.coordinates?.length ? fileData.coordinates : acquisitionCoordinates;
  const { data } = useResult();
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingImages, setIsLoadingImages] = useState(false);
  const [depthImageUrl, setDepthImageUrl] = useState('');
  const [satelliteMs, setSatelliteMs] = useState([]);
  const [tideChartOpen, setTideChartOpen] = useState(false);
  const [metricsCollapsed, setMetricsCollapsed] = useState(false);
  const [binsCollapsed, setBinsCollapsed] = useState(false);
  const [mapType, setMapType] = useLocalStorage('bathymetryMapType', 'roadmap');
  const [labelsVisible, setLabelsVisible] = useLocalStorage('bathymetryMapLabels', true);
  const { shorelineThemeMode } = useThemeMode();
  const mapRef = useRef();
  const boundsRef = useRef();
  const ROI = useRef();
  const bdsImage = useRef();
  const bdsImageNoTide = useRef();
  const acquisitionPointsRef = useRef([]);
  const [validationBins, setValidationBins] = useState([]);
  const [processingError, setProcessingError] = useState(null);
  const [isLoadingGeoJson, setIsLoadingGeoJson] = useState(false);
  const [isSavingModel, setIsSavingModel] = useState(false);
  const trainedModelRef = useRef(null);
  const [metricsReport, setMetricsReport] = useState({
    BDS_MODEL: data?.modelSelected,
    IMAGE_SOURCE: satelliteMs[0],
    RUN_MODE: data?.typeImageProcess || 'SINGLE',
    SCALE_M: satelliteMs[1]?.opticalResolution ?? '—',
    CRS: data?.coordinatesSystem || '—',
    n: '—',
    rmse: '—',
    mae: '—',
    bias: '—',
    r2: '—',
    SI: '—',
    nRMSE: '—',
    skill: '—',
  });
  const overlayRef = useRef(null);
  const imageOverlayRef = useRef(null);
  const [normalImageUrl, setNormalImageUrl] = useState('');
  const [overlayVisible, setOverlayVisible] = useState(true);
  const [imageRGBVisible, setImageRGBVisible] = useState(false);
  const [csvExportProgress, setCsvExportProgress] = useState('');
  const hasTide = hasTideSeries(fileData, data);

  /**
   * @function handleMapTypeChange
   * @description Switches the map's base layer from the "Mapa de fundo" panel.
   * @param {String} type - `'roadmap'` or `'satellite'`.
   * @memberof module:Bathymetry
   * @returns {void}
   */
  const handleMapTypeChange = (type) => {
    setMapType(type);
    mapRef.current?.setMapTypeId(type);
  };

  useEffect(() => {
    if (!mapRef.current) return;
    mapRef.current.setOptions({ styles: buildMapStyles({ themeMode: shorelineThemeMode, mapType, labelsVisible }) });
  }, [shorelineThemeMode, mapType, labelsVisible]);

  /**
   * @function toggleOverlayAndRGBOverlay
   * @description Swaps the two ground overlays: the modelled depth and the true-colour imagery
   * are never shown at once, so one toggle drives both.
   * @memberof module:Bathymetry
   * @returns {void}
   */
  const toggleOverlayAndRGBOverlay = () => {
    const overlayNewState = !overlayVisible;
    const rgbOverlayNewState = !imageRGBVisible;
    imageOverlayRef.current?.setMap(rgbOverlayNewState ? mapRef.current : null);
    overlayRef.current?.setMap(overlayNewState ? mapRef.current : null);
    setImageRGBVisible(rgbOverlayNewState);
    setOverlayVisible(overlayNewState);
  };

  /**
   * @function computeMetrics
   * @description Scores the run and puts the result on screen - see
   * {@link computeDepthMetrics}. When the tide correction is on, the gauge level at each
   * acquisition is read as well, for the chart to mark.
   * @param {Object} missionInfo - The mission, for its name and its optical resolution.
   * @param {String} type - The imagery's processing level, TOA or SR.
   * @param {Object} metricsCoefDict - What the model fitting already reported.
   * @memberof module:Bathymetry
   * @returns {Promise<void>}
   */
  const computeMetrics = async (missionInfo, type, metricsCoefDict) => {
    const scored = await computeDepthMetrics({
      image: bdsImage.current,
      region: roiRef?.current,
      data,
      fileData,
      missionInfo,
      type,
      metricsCoefDict,
    });
    if (!scored) return;
    if (scored.bins) setValidationBins(scored.bins);
    setMetricsReport(scored.metrics);
    if (!hasTide) return;
    acquisitionPointsRef.current = mergeTidePoints(acquisitionPointsRef.current, await probeTidePoints(fileData, data));
  };

  /**
   * @function downloadCSVmetrics
   * @description Downloads the metrics table and the per-band breakdown as one CSV.
   * @memberof module:Bathymetry
   * @returns {Promise<void>}
   */
  const downloadCSVmetrics = async () => {
    setIsLoading(true);
    await new Promise((r) => setTimeout(r, 0));
    try {
      downloadCSV(metricsCsv(metricsReport, validationBins), `model_metrics_${data.modelSelected}_${satelliteMs[0]}`);
    } catch (err) {
      console.error('Erro ao gerar métricas:', err);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * @function exportSessionState
   * @description Downloads the analysis's configuration as JSON, for the wizard's "import
   * session" to replay later - so testing a result does not mean redoing the whole wizard.
   * @see {@link buildSessionExport}
   * @memberof module:Bathymetry
   * @returns {void}
   */
  const exportSessionState = () => {
    exportSession(buildSessionExport({ data, fileData }));
  };

  useRegisterSessionExport('bathymetry', !isLoading ? exportSessionState : null);

  /**
   * @function exportGeoJsonLayers
   * @description Writes the area, the surveyed soundings and the modelled depth to a GeoJSON.
   * The depth is a raster, so it leaves as one point per pixel - see {@link fetchDepthPoints},
   * which pages the request and coarsens rather than truncates. That takes a moment; if it fails
   * the file is still written with the area and the soundings, since losing the export over the
   * depth would be worse than writing it without.
   * @memberof module:Bathymetry
   * @returns {Promise<void>}
   */
  const exportGeoJsonLayers = async () => {
    let depthPoints = null;
    if (bdsImage.current && roiRef.current && satelliteMs?.[1]?.opticalResolution) {
      setIsLoadingGeoJson(true);
      try {
        depthPoints = await fetchDepthPoints(addPredDepthStd(bdsImage.current), {
          region: roiRef.current,
          scale: Number(satelliteMs[1].opticalResolution),
        });
        if (depthPoints.coarsened) {
          notifyError(
            t('forms.acquisition.5.bathymetry.pdfReport.depthCoarsened', {
              requested: depthPoints.requested,
              written: depthPoints.features.length,
              scale: depthPoints.scale,
            }),
          );
        }
      } catch (err) {
        console.error('exportGeoJsonLayers: could not vectorise the depth', err);
        notifyError(mapGeeError(err, t, 'geoJsonDepthFailed'));
      } finally {
        setIsLoadingGeoJson(false);
      }
    }
    if (!exportGeoJson({ fileData, depthPoints })) notifyError(t('forms.acquisition.geoJsonExport.empty'));
  };
  useRegisterGeoJsonExport('bathymetry', !isLoading ? exportGeoJsonLayers : null);

  /**
   * @function exportPdfReport
   * @description Writes the whole analysis to one PDF.
   * The maps are fetched first, because they come from Earth Engine as cross-origin thumbnails
   * and a browser that refuses to release one must cost that image and not the document - see
   * {@link loadImageForReport}, which resolves rather than rejects.
   * @memberof module:Bathymetry
   * @returns {Promise<void>}
   */
  const exportPdfReport = async () => {
    try {
      const text = reportText(t, data);
      const images = await Promise.all([
        loadImageForReport(depthImageUrl, text.depthCaption),
        loadImageForReport(normalImageUrl, text.imageCaption),
      ]);
      const report = buildBathymetryReport({
        data,
        fileData,
        metrics: metricsReport,
        bins: validationBins,
        images,
        text,
        metricLabel: (field) =>
          t(`forms.acquisition.5.bathymetry.bathymetryProcessing.metrics.${field}`, { defaultValue: field }),
        metricFormat: formatMetricValue,
      });
      renderReport(report).save(reportFileName(REPORT_MODULE));
    } catch (error) {
      console.error('exportPdfReport error:', error);
      const wording = t('forms.acquisition.5.bathymetry.pdfReport.failed');
      setProcessingError(wording);
      notifyError(wording);
    }
  };
  useRegisterPdfExport(REPORT_MODULE, !isLoading && Object.keys(metricsReport).length > 0 ? exportPdfReport : null);

  /**
   * @function saveTrainedModel
   * @description Writes the model the last run fitted to a file, so it can be applied to other
   * imagery without refitting.
   * The trees are read out of Earth Engine here rather than at training time, because reading
   * them is a request of its own and most runs never save. What goes in the file besides the
   * trees is everything needed to feed them the same inputs - see {@link buildModelFile}.
   * @memberof module:Bathymetry
   * @returns {Promise<void>}
   */
  const saveTrainedModel = async () => {
    const trained = trainedModelRef.current;
    if (!trained) {
      notifyError(t('forms.acquisition.5.bathymetry.modelStore.nothingToSave'));
      return;
    }
    setIsSavingModel(true);
    try {
      const kinds = kindsFor(trained.model);
      const members = await Promise.all(trained.classifiers.map((classifier) => eeEval(treesOf(classifier))));
      const file = buildModelFile({
        model: trained.model,
        predictors: trained.predictors,
        params: trained.params,
        trees: Object.fromEntries(kinds.map((kind, index) => [memberKey(kind), members[index]])),
        context: presetContextOf(getSelectedSatellite(), satelliteMs?.[0], data.bands),
        trainedOn: {
          area: fileData?.kmlFileName ?? null,
          surveyDate: fileData?.date ?? null,
          imageDate: data?.imageDate ?? null,
          samples: trained.samples ?? null,
          crs: fileData?.coordinatesSystem ?? null,
        },
        exportedAt: new Date().toISOString(),
      });
      exportJSON(file, `cassie-bathymetry-model-${new Date().toISOString().slice(0, 10)}.json`);
    } catch (error) {
      console.error('saveTrainedModel error:', error);
      const wording = mapGeeError(error, t, 'genericError');
      setProcessingError(wording);
      notifyError(wording);
    } finally {
      setIsSavingModel(false);
    }
  };

  /**
   * @function downloadImages
   * @description Downloads the run as GeoTIFFs: the modelled depth, and the true-colour imagery
   * it was derived from, both clipped to the area at the mission's own resolution.
   * @memberof module:Bathymetry
   * @returns {Promise<void>}
   */
  const downloadImages = async () => {
    setIsLoadingImages(true);
    await new Promise((r) => setTimeout(r, 0));
    try {
      if (!bdsImage.current) throw new Error('bdsImage.current não está pronto.');
      if (!roiRef?.current) throw new Error('roiRef.current não está pronto.');
      if (!satelliteMs?.[1]?.opticalResolution) throw new Error('satelliteMs não definido.');
      const scale = Number(satelliteMs[1].opticalResolution);
      const tag = `${data.modelSelected}_${satelliteMs[0]}`;
      const predImg = addPredDepthStd(bdsImage.current).select('pred_depth').toFloat();
      await downloadEEImage(predImg, roiRef.current, scale, `predicted_depth_${tag}`);
      const { red, green, blue } = satelliteMs[1].bands;
      const workingRGB = ee.Image(data.image).select([red, green, blue]).toFloat().clip(roiRef.current);
      await downloadEEImage(workingRGB, roiRef.current, scale, `working_image_rgb_${tag}`);
    } catch (err) {
      console.error('Error while downloading images:', err);
    } finally {
      setIsLoadingImages(false);
    }
  };

  /**
   * @function exportPixelsToCSV
   * @description Downloads every pixel of the area as a CSV row - see {@link collectPixelRows},
   * which also builds the file the tree models are trained from.
   * An area too large for one request is split into parts first, and the user is told so, since
   * the export then takes several round trips rather than one.
   * @memberof module:Bathymetry
   * @returns {Promise<void>}
   */
  const exportPixelsToCSV = async () => {
    if (!bdsImage.current || !roiRef?.current || !satelliteMs?.[1]?.opticalResolution) {
      return console.error('[exportPixelsToCSV] o resultado ainda não está pronto.');
    }
    const scale = Number(satelliteMs[1].opticalResolution);
    const plan = await planRegionParts({ region: roiRef.current, scale, crs: fileData.coordinatesSystem });
    if (plan.parts > 1) {
      dispatch(
        Snack.warn(
          'csv-export-split-warning',
          t('forms.acquisition.5.bathymetry.bathymetryProcessing.csvExportSplitWarning', {
            pixels: Math.round(plan.estimatedPixels).toLocaleString(),
            limit: SAFE_PIXEL_LIMIT.toLocaleString(),
            parts: plan.parts,
          }),
        ),
      );
    }
    setIsLoading(true);
    setCsvExportProgress('');
    await new Promise((resolve) => setTimeout(resolve, 0));
    try {
      const { csv } = await collectPixelRows({
        depthImage: bdsImage.current,
        sourceImage: data.image,
        availableBands: await eeEval(bdsImage.current.bandNames()),
        data,
        fileData,
        missionBands: satelliteMs?.[1]?.bands,
        scale,
        region: roiRef.current,
        plan,
        hasTide,
        onPart: (current, total) =>
          setCsvExportProgress(
            t('forms.acquisition.5.bathymetry.bathymetryProcessing.csvExportProgress', { current, total }),
          ),
      });
      downloadCSV(csv, `predicted_depth_${data.modelSelected}_${satelliteMs[0]}`);
    } catch (error) {
      console.error('Erro ao baixar CSV pixels:', error);
    } finally {
      setIsLoading(false);
      setCsvExportProgress('');
    }
  };

  /**
   * @function displayROI
   * @description Draws the area of interest on the map and frames the view to it. Called once,
   * when the map instance is ready.
   * @memberof module:Bathymetry
   * @returns {void}
   */
  const displayROI = () => {
    if (!coordinates || !mapRef.current) return;
    ROI.current = new window.google.maps.Polygon({
      paths: aoiPaths(coordinates),
      geodesic: true,
      fillColor: 'transparent',
      strokeColor: '#00A391',
      strokeOpacity: 0.6,
      strokeWeight: 5,
      clickable: false,
    });
    ROI.current.setMap(mapRef.current);
    boundsRef.current = new window.google.maps.LatLngBounds();
    aoiPoints(coordinates).forEach(([lng, lat]) => boundsRef.current.extend({ lat, lng }));
    mapRef.current.setCenter(boundsRef.current.getCenter());
    mapRef.current.fitBounds(boundsRef.current, { padding: 50 });
  };

  useEffect(() => {
    const run = async () => {
      if (
        !data?.image ||
        !roiRef?.current ||
        !data?.bands?.length ||
        !fileData?.coordinatesSystem ||
        !data?.missionInfo
      ) {
        console.warn('[Bathymetry] result effect skipped, missing:', {
          image: !data?.image,
          roi: !roiRef?.current,
          bands: !data?.bands?.length,
          coordinatesSystem: !fileData?.coordinatesSystem,
          missionInfo: !data?.missionInfo,
        });
        return;
      }
      setIsLoading(true);
      setProcessingError(null);
      await new Promise((resolve) => setTimeout(resolve, 0));
      try {
        const type = window.sessionStorage.getItem('typeSatellite');
        const mission = data.missionInfo;
        const RGB = data.rgb || data.bands || [mission.bands.red, mission.bands.green, mission.bands.blue];
        const mismatch = data.presetMode
          ? presetMismatch(data.presetContext, presetContextOf(getSelectedSatellite(), type, data.bands))
          : null;
        if (mismatch) {
          notifyError(t('forms.acquisition.5.bathymetry.presetMismatch', mismatch));
          setIsLoading(false);
          return;
        }
        setSatelliteMs([type, mission]);
        const cleanImage = data.image.select(RGB).toFloat();
        let metricsCoefDict;

        if (isTreeModel(data.modelSelected)) {
          const fitted = await runTreeModel({
            data,
            fileData,
            region: roiRef.current,
            mission,
            type,
            reducer: getTrainingDepthReducer(data?.trainingDataReducer?.toLowerCase()),
            t,
          });
          if (fitted.error) {
            if (fitted.error.persist) setProcessingError(fitted.error.message);
            notifyError(fitted.error.message);
            setIsLoading(false);
            return;
          }
          if (fitted.warning) notifyError(fitted.warning);
          if (fitted.bins) setValidationBins(fitted.bins);
          if (fitted.trained) trainedModelRef.current = fitted.trained;
          bdsImage.current = fitted.image;
          metricsCoefDict = fitted.coefficients;
        } else {
          const fitted = await fitAnalyticModel({
            model: data.modelSelected,
            cleanImage,
            RGB,
            data,
            samples:
              data?.sampledImage && !data?.presetMode
                ? ee
                    .FeatureCollection(data.sampledImage)
                    .filter(ee.Filter.lte('or_depth', data.depthLightExtinction || 0))
                : null,
          });
          bdsImage.current = fitted.image;
          metricsCoefDict = fitted.coefficients;
        }

        bdsImageNoTide.current = ee.Image(bdsImage.current);
        bdsImage.current = applyTideToDepth(bdsImage.current, { data, fileData, fallbackTimestamp: cleanImage });
        const waterMask = cleanImage.select(RGB).mask().reduce(ee.Reducer.min());
        const bdsPlot = addPredDepthStd(bdsImageNoTide.current.updateMask(waterMask))
          .select('pred_depth')
          .clip(roiRef.current)
          .clamp(0, 20);
        const preview = eeUrl(bdsPlot, 'getThumbURL', {
          region: roiRef.current.bounds(),
          scale: previewScale(coordinates, getSelectedSatellite().name === 'Landsat' ? 30 : 10),
          palette: DEPTH_PALETTE,
          max: 20,
          min: 0,
          format: 'png',
          crs: PREVIEW_CRS,
        }).then(setDepthImageUrl);
        setNormalImageUrl(data.thumbImage);
        await Promise.all([preview, computeMetrics(mission, type, metricsCoefDict)]);
        setIsLoading(false);
      } catch (err) {
        console.error('Erro no processamento:', err);
        const wording = mapGeeError(
          err,
          t,
          'genericError',
          isTreeModel(data.modelSelected) ? { errorMemory: 'treeErrorMemory' } : {},
        );
        setProcessingError(wording);
        notifyError(wording);
        setIsLoading(false);
      }
    };
    run();
  }, [
    data?.image,
    data?.sampledImage,
    data?.bands,
    data?.modelSelected,
    data?.treeParams,
    data?.validationSplit,
    +data?.missionInfo,
    fileData?.coordinatesSystem,
    data?.tideConfig,
    fileData?.tideData,
    fileData?.timezoneOffset,
  ]);

  useEffect(() => {
    if (!(mapRef.current && depthImageUrl && normalImageUrl && roiRef.current)) return;
    roiRef.current.bounds().evaluate((result, error) => {
      if (error) return console.error('Erro ao obter bounds do EE:', error);
      const coords = result.coordinates[0];
      const lons = coords.map((p) => p[0]);
      const lats = coords.map((p) => p[1]);
      const imageBounds = new window.google.maps.LatLngBounds(
        { lat: Math.min(...lats), lng: Math.min(...lons) },
        { lat: Math.max(...lats), lng: Math.max(...lons) },
      );
      mapRef.current.fitBounds(imageBounds);
      overlayRef.current?.setMap(null);
      overlayRef.current = new window.google.maps.GroundOverlay(depthImageUrl, imageBounds, {
        opacity: 1,
        clickable: false,
        map: mapRef.current,
      });
      setOverlayVisible(true);
      imageOverlayRef.current?.setMap(null);
      imageOverlayRef.current = new window.google.maps.GroundOverlay(normalImageUrl, imageBounds, {
        opacity: 1,
        clickable: false,
        map: null,
      });
      setImageRGBVisible(false);
    });
  }, [depthImageUrl, normalImageUrl, roiRef]);

  const tideSeries = normalisedTideSeries(fileData?.tideData);

  return (
    <Box className={classes.wrapper}>
      {(isLoading || isLoadingGeoJson) && (
        <ActivityIndicator
          forceShow={true}
          title={t(isLoadingGeoJson ? 'forms.core.loading.depthZones' : 'forms.core.loading.depth')}
        />
      )}
      <Box className={classes.leftPanel}>
        <OverlayPanelsToggle side="left" />
        <Card className={classes.cardDepthImage}>
          <Button color="primary" onClick={exportPixelsToCSV} disabled={isLoading}>
            {t('forms.acquisition.5.bathymetry.bathymetryProcessing.generateCsv')}
          </Button>
          {csvExportProgress !== '' && <Typography variant="caption">{csvExportProgress}</Typography>}
          <Button color="primary" onClick={downloadCSVmetrics} disabled={isLoading}>
            {t('forms.acquisition.5.bathymetry.bathymetryProcessing.generateMetricsCsv')}
          </Button>
          <Button
            color="primary"
            onClick={saveTrainedModel}
            disabled={isSavingModel || isLoading || !isTreeModel(data?.modelSelected)}>
            {isSavingModel
              ? t('forms.acquisition.5.bathymetry.modelStore.saving')
              : t('forms.acquisition.5.bathymetry.modelStore.save')}
          </Button>
          <Button color="secondary" onClick={downloadImages} disabled={isLoadingImages || isLoading}>
            {isLoadingImages
              ? t('forms.acquisition.5.bathymetry.bathymetryProcessing.downloadingImages')
              : t('forms.acquisition.5.bathymetry.bathymetryProcessing.downloadGeotiff')}
          </Button>
          {hasTide && (
            <Button
              color="primary"
              onClick={() => setTideChartOpen(true)}
              disabled={isLoading || acquisitionPointsRef.current.length === 0}
              startIcon={<ShowChartIcon />}>
              {t('forms.acquisition.5.bathymetry.viewTideChart', { defaultValue: 'Tide chart' })}
            </Button>
          )}
        </Card>
        {processingError && (
          <Card className={classes.cardMetrics}>
            <Alert severity="error" onClose={() => setProcessingError(null)}>
              {processingError}
            </Alert>
          </Card>
        )}
        {Object.keys(metricsReport).length > 0 && (
          <Box className={classes.metricsSection}>
            <ResultPanel
              classes={classes}
              title={t('forms.acquisition.5.bathymetry.bathymetryProcessing.metricsReport')}
              collapsed={metricsCollapsed}
              onToggle={() => setMetricsCollapsed((prev) => !prev)}
              busy={isLoading}>
              <Box className={classes.metricsGrid}>
                {Object.entries(metricsReport).map(([key, value]) => (
                  <Box key={key} className={classes.metricRow}>
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                      {t(`forms.acquisition.5.bathymetry.bathymetryProcessing.metrics.${key}`, { defaultValue: key })}
                    </Typography>
                    <Tooltip title={LONG_FIELDS.has(key) && value != null ? String(value) : ''} placement="top">
                      <Typography
                        variant="caption"
                        style={{
                          textAlign: 'right',
                          minWidth: 0,
                          ...(LONG_FIELDS.has(key)
                            ? { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }
                            : { wordBreak: 'break-word' }),
                        }}>
                        {value != null ? formatMetricValue(key, value) : '—'}
                      </Typography>
                    </Tooltip>
                  </Box>
                ))}
              </Box>
            </ResultPanel>
            {validationBins.length > 0 && (
              <ResultPanel
                classes={classes}
                title={t('forms.acquisition.5.bathymetry.bathymetryProcessing.errorByDepth')}
                collapsed={binsCollapsed}
                onToggle={() => setBinsCollapsed((prev) => !prev)}>
                <Box className={classes.metricsGrid}>
                  {validationBins.map((bin) => (
                    <Box key={bin.bin} className={classes.metricRow}>
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                        {bin.label}
                        <Typography variant="caption" component="span" style={{ opacity: 0.6 }}>
                          {` n=${bin.n}`}
                        </Typography>
                      </Typography>
                      <Typography variant="caption" style={{ textAlign: 'right' }}>
                        {bin.rmse != null ? `${bin.rmse.toFixed(2)} m` : '—'}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </ResultPanel>
            )}
          </Box>
        )}
      </Box>
      <Box className={classes.rightPanel}>
        <OverlayPanelsToggle side="right" />
        <BackgroundMapPanel
          mapType={mapType}
          onMapTypeChange={handleMapTypeChange}
          labelsVisible={labelsVisible}
          onLabelsVisibleChange={setLabelsVisible}
          t={t}
        />
        <Card className={classes.cardToggle}>
          <Box display="flex" flexDirection="row" alignItems="center" style={{ gap: 4 }}>
            <IconButton onClick={toggleOverlayAndRGBOverlay} disabled={!depthImageUrl || isLoading} size="small">
              {overlayVisible ? <Visibility fontSize="small" /> : <VisibilityOff fontSize="small" />}
            </IconButton>
            <Typography variant="body2">
              {overlayVisible
                ? t('forms.acquisition.5.bathymetry.bathymetryProcessing.hideDepthImage')
                : t('forms.acquisition.5.bathymetry.bathymetryProcessing.showDepthImage')}
            </Typography>
          </Box>
          <Box display="flex" flexDirection="row" alignItems="center" style={{ gap: 4 }}>
            <IconButton onClick={toggleOverlayAndRGBOverlay} disabled={!normalImageUrl || isLoading} size="small">
              {imageRGBVisible ? <Visibility fontSize="small" /> : <VisibilityOff fontSize="small" />}
            </IconButton>
            <Typography variant="body2">
              {imageRGBVisible
                ? t('forms.acquisition.5.bathymetry.bathymetryProcessing.disableImage')
                : t('forms.acquisition.5.bathymetry.bathymetryProcessing.enableImage')}
            </Typography>
          </Box>
        </Card>
      </Box>
      <Box className={classes.legend}>
        <Typography variant="caption" style={{ fontWeight: 600, marginBottom: 4 }}>
          {t('forms.acquisition.5.bathymetry.legendTitle')}
        </Typography>
        <Box className={classes.legendBar} />
        <Box className={classes.legendLabels}>
          <span>0</span>
          <span>10</span>
          <span>20</span>
        </Box>
      </Box>
      <GoogleMap
        onLoad={(map) => {
          mapRef.current = map;
          map.setMapTypeId(mapType);
          displayROI();
        }}
        style={{ position: 'absolute' }}
        mapOptions={{
          streetViewControl: false,
          mapTypeControl: false,
        }}
      />
      <ReturnButtons lastStepPath="/main/bathymetry/4" />
      {hasTide && (
        <TideChartModal
          open={tideChartOpen}
          onClose={() => setTideChartOpen(false)}
          tideSeries={tideSeries}
          acquisitionPoints={acquisitionPointsRef.current}
          location={data?.location ?? ''}
        />
      )}
    </Box>
  );
};

/**
 * @function useStyles-Result
 * @description Hook for styling using Material-UI's makeStyles. Defines CSS styles .
 * @param {Object} theme - Material-UI theme containing color, spacing, and other configurations
 * @memberof module:Bathymetry
 * @returns {Object} Object containing compiled CSS classes for component usage
 */
const useStyles = makeStyles((theme) => ({
  leftPanel: {
    position: 'absolute',
    top: theme.spacing(8),
    left: theme.spacing(2),
    zIndex: 1001,
    display: 'flex',
    flexDirection: 'column',
    width: OVERLAY_PANEL_WIDTH,
    gap: 16,
    maxHeight: `calc(100% - ${theme.spacing(8)}px - ${OVERLAY_BOTTOM_SAFE_AREA})`,
    overflowY: 'auto',
    ...OVERLAY_COLUMN_LAYOUT,
  },
  metricsSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  legend: {
    position: 'absolute',
    bottom: theme.spacing(3),
    right: theme.spacing(14),
    zIndex: 1001,
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    padding: '10px 14px',
    borderRadius: 8,
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    display: 'flex',
    flexDirection: 'column',
    minWidth: 220,
  },
  legendBar: {
    height: 16,
    borderRadius: 4,
    marginBottom: 6,
    background: 'linear-gradient(to right, #00FFFF, #0000FF)',
  },
  legendLabels: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: 12,
    color: theme.palette.text.secondary,
  },
  rightPanel: {
    position: 'absolute',
    top: theme.spacing(8),
    right: theme.spacing(2),
    zIndex: 1001,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: 16,
    maxHeight: `calc(100vh - ${theme.spacing(8) + MAP_CONTROLS_SAFE_AREA}px)`,
    overflowY: 'auto',
    ...OVERLAY_COLUMN_LAYOUT,
  },
  wrapper: {
    maxHeight: '100vh',
    flexGrow: 1,
    position: 'relative',
    overflow: 'hidden',
  },
  cardToggle: {
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    borderRadius: 8,
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    padding: '6px 16px',
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  cardDepthImage: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: 8,
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    padding: '10px 22px',
    display: 'flex',
    alignItems: 'flex-start',
    flexDirection: 'column',
    gap: 8,
    fontSize: '14px',
    fontWeight: 500,
    textTransform: 'none',
    color: theme.palette.text.primary,
  },
  cardMetrics: {
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    borderRadius: 8,
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    width: '100%',
  },
  metricsPadding: {
    padding: '10px 14px',
  },
  metricsGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  metricRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 8,
    minWidth: 0,
  },
}));

/**
 * @function ProcessingPage
 * @description Bathymetry result page entry point. Builds the module-scoped light/dark theme and
 * provides it to `BathymetryResultContent`, which is where every `makeStyles` call on this screen
 * lives. See that component for why the split is required rather than cosmetic.
 * @memberof module:Bathymetry
 * @returns {JSX.Element}
 */
const ProcessingPage = () => {
  const outerTheme = useTheme();
  const { shorelineThemeMode } = useThemeMode();
  const bathymetryTheme = useMemo(
    () => createShorelineTheme(shorelineThemeMode, outerTheme),
    [outerTheme, shorelineThemeMode],
  );
  return (
    <ThemeProvider theme={bathymetryTheme}>
      <BathymetryResultContent />
    </ThemeProvider>
  );
};

export default ProcessingPage;
